import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getContext } from 'pages/requestForm/selectors/pageSelectors';
import {
    getFieldsApprovalPermissionBySystemPurpose,
    getRequiredFields,
} from 'pages/requestForm/selectors/requestSelectors';
import { getXeroContext, XeroRequest } from 'pages/requestForm/selectors/xero';
import { useMemo } from 'react';

export const useXeroFieldSettings = (request: XeroRequest) => {
    const permissionsItem = useXeroItemFieldState(request);
    const permissionsAccount = useXeroAccountFieldState(request);
    const permissionsTax = useXeroTaxFieldState(request);
    const permissionsTaxAmount = useXeroTaxAmountFieldState(request);

    const [description, quantity, unitPrice, discount] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [
            domain.FieldSystemPurpose.XeroLineDescription,
            domain.FieldSystemPurpose.XeroLineQty,
            domain.FieldSystemPurpose.XeroLineUnitPrice,
            domain.FieldSystemPurpose.XeroLineDiscount,
        ])
    );

    return useMemo(
        () => ({
            item: getXeroFieldStateByPermissions(permissionsItem),
            description: getXeroFieldStateByPermissions(description),
            qty: getXeroFieldStateByPermissions(quantity),
            unitPrice: getXeroFieldStateByPermissions(unitPrice),
            disc: getXeroFieldStateByPermissions(discount),
            account: getXeroFieldStateByPermissions(permissionsAccount),
            tax: getXeroFieldStateByPermissions(permissionsTax),
            taxAmount: getXeroFieldStateByPermissions(permissionsTaxAmount),
            tracking: domain.DocumentFieldState.Editable,
            amount: domain.DocumentFieldState.ReadOnly,
        }),
        [
            description,
            discount,
            permissionsAccount,
            permissionsItem,
            permissionsTax,
            permissionsTaxAmount,
            quantity,
            unitPrice,
        ]
    );
};

const getXeroFieldStateByPermissions = (permissions: { required: boolean; disabled: boolean }) => {
    if (permissions.disabled) {
        return domain.DocumentFieldState.ReadOnly;
    }

    if (permissions.required) {
        return domain.DocumentFieldState.Mandatory;
    }

    return domain.DocumentFieldState.Editable;
};

const useXeroItemFieldState = (request: XeroRequest): { required: boolean; disabled: boolean } => {
    const purpose = domain.FieldSystemPurpose.XeroItem;

    const context = useSelector(getContext);
    const requiredFields = useSelector((state) => getRequiredFields(state, request));
    const [permissions] = useSelector((state) => getFieldsApprovalPermissionBySystemPurpose(state, request, [purpose]));

    const field = useMemo(
        () => context.fields.find((field) => field.systemPurpose === purpose),
        [context.fields, purpose]
    );
    const isRequired = Boolean(field && requiredFields.fieldIds.includes(field.id));
    const isDisabled = Boolean(field && context.readonlyFieldIds.includes(field.id));

    return {
        disabled: permissions.disabled || isDisabled,
        required: permissions.required || isRequired,
    };
};

const useXeroAccountFieldState = (request: XeroRequest): { required: boolean; disabled: boolean } => {
    const purpose = domain.FieldSystemPurpose.XeroAccount;

    const context = useSelector(getContext);
    const requiredFields = useSelector((state) => getRequiredFields(state, request));
    const [permissions] = useSelector((state) => getFieldsApprovalPermissionBySystemPurpose(state, request, [purpose]));

    const field = useMemo(
        () => context.fields.find((field) => field.systemPurpose === purpose),
        [context.fields, purpose]
    );
    const isRequired = Boolean(field && requiredFields.fieldIds.includes(field.id));

    return {
        disabled: permissions.disabled,
        required: permissions.required || isRequired,
    };
};

const useXeroTaxFieldState = (request: XeroRequest): { required: boolean; disabled: boolean } => {
    const purpose = domain.FieldSystemPurpose.XeroTax;

    const [permissions] = useSelector((state) => getFieldsApprovalPermissionBySystemPurpose(state, request, [purpose]));

    return {
        disabled: permissions.disabled || request.details.lineAmountType === domain.LineAmountType.NoTax,
        required: permissions.required,
    };
};

const useXeroTaxAmountFieldState = (request: XeroRequest): { required: boolean; disabled: boolean } => {
    const purpose = domain.FieldSystemPurpose.XeroLineTaxAmount;

    const context = useSelector(getContext);
    const xeroContext = useSelector(getXeroContext);
    const requiredFields = useSelector((state) => getRequiredFields(state, request));
    const [permissions] = useSelector((state) => getFieldsApprovalPermissionBySystemPurpose(state, request, [purpose]));

    const field = useMemo(
        () => context.fields.find((field) => field.systemPurpose === purpose),
        [context.fields, purpose]
    );
    const isRequired = Boolean(field && requiredFields.fieldIds.includes(field.id));

    return xeroContext.requiresTaxAdjustments
        ? {
              disabled: false,
              required: false,
          }
        : {
              disabled: permissions.disabled || request.details.lineAmountType === domain.LineAmountType.NoTax,
              required: permissions.required || isRequired,
          };
};
